import React, { useMemo, useState } from "react";
import { ComposedChart, Area, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Tile, Badge } from "../Primitives";
import { syn } from "../../lib/origin.jsx";
import { DEMAND_SERIES, accuracyOf } from "../../lib/dataPilot";
import { useChartColors } from "../../lib/chartColors";
import { formatNumber, cn } from "../../lib/utils";
import { ChartTip, tickStyle } from "./chartBits";

const ORIGIN = syn("POS demand history (units) & forecast model output");

export default function DemandForecastTile() {
  const C = useChartColors();
  const [range, setRange] = useState("30D");
  const data = DEMAND_SERIES[range];
  const stats = useMemo(() => {
    const total = data.reduce((s, p) => s + p.actual, 0);
    const fc = data.reduce((s, p) => s + p.forecast, 0);
    return { total, avg: total / data.length, peak: Math.max(...data.map(p => p.actual)), acc: accuracyOf(data), bias: (fc - total) / total };
  }, [data]);
  const unit = range === "30D" ? "day" : "month";

  return (
    <Tile className="col-span-12 lg:col-span-8" testid="demand-vs-forecast" title="Demand vs Forecast · units" origin={ORIGIN}
          action={<div className="flex items-center gap-2">
            <div className="inline-flex border border-border rounded-sm overflow-hidden text-[10px] uppercase tracking-widest font-semibold" data-testid="demand-range">
              {["30D", "12M", "48M"].map(r => (
                <button key={r} data-testid={`range-${r}`} onClick={() => setRange(r)}
                        className={cn("px-3 h-7 transition-colors", range === r ? "bg-primary text-primary-fg" : "bg-surface-2 text-muted hover:bg-surface")}>
                  {r}
                </button>
              ))}
            </div>
            <Badge status={stats.acc >= 0.85 ? "success" : "warning"} testid="demand-accuracy">{(stats.acc * 100).toFixed(1)}% accuracy</Badge>
          </div>}>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <Stat label={`${range} demand`} value={`${formatNumber(stats.total, { compact: true })} u`} />
        <Stat label={`Avg per ${unit}`} value={`${formatNumber(Math.round(stats.avg), { compact: true })} u`} />
        <Stat label="Forecast bias" value={<span className={Math.abs(stats.bias) < 0.05 ? "text-success" : "text-warning"}>{stats.bias > 0 ? "+" : ""}{(stats.bias * 100).toFixed(1)}%</span>} />
      </div>
      <div style={{ height: 240 }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: -6, bottom: 0 }}>
            <defs>
              <linearGradient id="dm-act" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.chart[0]} stopOpacity="0.4" />
                <stop offset="100%" stopColor={C.chart[0]} stopOpacity="0" />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={C.border} strokeDasharray="2 4" />
            <XAxis dataKey="x" tick={tickStyle} stroke={C.borderStrong} interval={range === "48M" ? 5 : range === "12M" ? 0 : "preserveStartEnd"} />
            <YAxis tick={tickStyle} stroke={C.borderStrong} tickFormatter={v => formatNumber(v, { compact: true })} />
            <Tooltip content={<ChartTip formatter={v => `${formatNumber(v)} u`} />} cursor={{ fill: C.surface2 }} />
            <Area type="monotone" dataKey="actual" name="Actual demand" stroke={C.chart[0]} strokeWidth={2.2} fill="url(#dm-act)" isAnimationActive={false} />
            <Line type="monotone" dataKey="forecast" name="Forecast" stroke={C.chart[1]} strokeWidth={1.8} strokeDasharray="5 4" dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Tile>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value text-lg mt-1.5">{value}</div>
    </div>
  );
}
